import { Expense } from '@/app/models/expense';
import { useExpenseQuery } from '@/app/services/expenseApi';
import searchIcon from '@/icons/search.svg';
import { Autocomplete, Box, InputAdornment, InputProps, TextField, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useCreateBatchExpenseExceptionMutation,
  useDeleteBatchExpenseExceptionMutation,
} from '@/app/services/expenseExceptionApi';
import { toast } from 'react-toastify';
import CancelOrConfirmDialog from '../common/dialogs/CancelOrConfirmDialog';
import BatchExceptionsTable from '../tables/BatchExceptionsTable';

type BatchExceptionDrawerProps = {
  onClose: () => void;
  add: boolean;
};

export default function BatchExceptionDrawer(props: BatchExceptionDrawerProps) {
  const { onClose, add } = props;

  const { t } = useTranslation(['exceptions', 'common']);

  const [confirmDialog, setConfirmDialog] = useState(false);
  const [expense, setExpense] = useState('');
  const [searchExpense, setSearchExpense] = useState('');
  const [selectedExpense, setSelectedExpense] = useState(null as unknown as Expense);
  const [addedItens, setAddedItens] = useState([] as Expense[]);

  const [addBatchExceptions] = useCreateBatchExpenseExceptionMutation();
  const [deleteBatchExceptions] = useDeleteBatchExpenseExceptionMutation();

  const { data: expenses } = useExpenseQuery({ searchTerm: searchExpense, bringOnlyNew: false, page: 0, size: 100 });

  const handleSubmitAdd = async () => {
    const addingCodes = addedItens.map((i) => i.code);
    const response = addBatchExceptions({ codes: addingCodes });
    if ('data' in response) {
      toast.success(
        t('general.exception.case.created.successfully', {
          position: toast.POSITION.TOP_RIGHT,
        }),
      );
    }
  };
  const handleSubmitRemove = async () => {
    const removingCodes = addedItens.map((i) => i.code);
    const response = deleteBatchExceptions({ codes: removingCodes });
    if ('data' in response) {
      toast.success(
        t('general.exception.case.removed.successfully', {
          position: toast.POSITION.TOP_RIGHT,
        }),
      );
    }
  };

  const handleAddToList = async (newExpense: Expense) => {
    const newExpenses = [...addedItens];
    newExpenses.push(newExpense);
    setAddedItens(newExpenses);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh )',
        maxHeight: 'calc(100vh )',
        flexGrow: 'inherit',
        background: '#FAFAFA',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 'inherit' }}>
        <div style={{ marginTop: '32px', marginBottom: '19px' }}>
          <Typography variant="h3">{add ? t('add.general.benefits') : t('remove.general.benefits')}</Typography>
          <Typography variant="subtitle1">
            {add
              ? t('by.inserting.the.benefits.below.all.existing.exception.cases.will.be.added')
              : t('by.inserting.the.benefits.below.they.will.be.removed.from.all.existing.exception.cases')}
          </Typography>
          <div style={{ display: 'flex', alignItems: 'end' }}>
            <Autocomplete
              freeSolo
              filterOptions={(option) => option}
              isOptionEqualToValue={(option, value) => option?.code === value?.code}
              style={{ width: '100%', marginRight: '10px', marginTop: '8px' }}
              size="small"
              defaultValue={selectedExpense}
              onChange={(event, newValue) => {
                setSelectedExpense(newValue as Expense);
              }}
              inputValue={expense}
              onInputChange={(event, newInputValue) => {
                setExpense(newInputValue);
                if (newInputValue.length >= 1) {
                  setSearchExpense(newInputValue);
                }
              }}
              options={expenses?.content || []}
              getOptionLabel={(option) => (typeof option === 'string' ? option : `${option.code}`)}
              renderOption={(params, option) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <Box component="li" sx={{ fontSize: '13px', '& > img': { mr: 2, flexShrink: 0 } }} {...params}>
                  {`${option.code}`}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  sx={{
                    '& input': {
                      padding: '0px',
                    },
                  }}
                  size="small"
                  style={{ width: 'inherit', height: '32px', fontSize: '13px' }}
                  ref={params.InputProps.ref}
                  InputProps={
                    {
                      ...params.inputProps,
                      endAdornment: (
                        <InputAdornment position="end">
                          <img src={searchIcon} alt={t('common:search.icon') as string} />
                        </InputAdornment>
                      ),
                      style: {
                        paddingRight: '10px',
                        paddingTop: '0px',
                        paddingBottom: '0px',
                        paddingLeft: '10px',
                        fontSize: '13px',
                      },
                    } as InputProps
                  }
                  autoFocus
                  placeholder={t('search.by.event.code') as string}
                />
              )}
            />
            <Button
              disabled={!selectedExpense}
              sx={{ marginLeft: '24px', marginRight: '16px' }}
              variant="tertiary"
              onClick={() => {
                handleAddToList(selectedExpense);
              }}
            >
              {t('common:+Add')}
            </Button>
          </div>
          <div style={{ display: 'flex', marginTop: '24px' }}>
            <div style={{ marginRight: '40px' }}>
              <Typography variant="subtitle2" sx={{ color: '#757575' }}>
                {t('common:code')}
              </Typography>
              <Typography variant="h3">{selectedExpense ? selectedExpense.code : '---'}</Typography>
            </div>
            <div style={{ marginRight: '40px' }}>
              <Typography variant="subtitle2" sx={{ color: '#757575' }}>
                {t('common:shareable')}
              </Typography>
              <Typography variant="h3">
                {(selectedExpense && (selectedExpense.isShareable ? t('common:yes') : t('common:no'))) || '---'}
              </Typography>
            </div>
            <div style={{ marginRight: '40px' }}>
              <Typography variant="subtitle2" sx={{ color: '#757575' }}>
                {t('common:financial')}
              </Typography>
              <Typography variant="h3">
                {(selectedExpense && (selectedExpense.isCredit ? t('credit') : t('debt'))) || '---'}
              </Typography>
            </div>
            <div style={{ marginRight: '40px' }}>
              <Typography variant="subtitle2" sx={{ color: '#757575' }}>
                {t('common:category')}
              </Typography>
              <Typography variant="h3">{selectedExpense ? selectedExpense.category?.name : '---'}</Typography>
            </div>
            <div style={{ marginRight: '40px' }}>
              <Typography variant="subtitle2" sx={{ color: '#757575' }}>
                {t('common:group')}
              </Typography>
              <Typography variant="h3">{selectedExpense ? selectedExpense.expenseGroup?.name : '---'}</Typography>
            </div>
          </div>
          <div style={{ width: '100%', border: '1px solid #CCCCCC', marginTop: '32px' }} />
          <BatchExceptionsTable rows={addedItens} />
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          marginTop: '16px',
          paddingBottom: '16px',
        }}
      >
        <Button
          variant="secondary"
          onClick={() => {
            onClose();
          }}
        >
          {t('common:cancel')}
        </Button>

        <Button
          disabled={addedItens.length <= 0}
          variant="primary"
          onClick={() => {
            setConfirmDialog(true);
          }}
          sx={{ marginLeft: '16px' }}
        >
          {t('common:save')}
        </Button>
      </div>
      <CancelOrConfirmDialog
        open={confirmDialog}
        title={add ? t('add.general.rule') : t('remove.general.rule')}
        contentText={
          add
            ? t('all.exceptions.will.be.added.to.all.projects.are.you.sure.you.want.to.edit')
            : t('all.exceptions.will.be.removed.from.all.projects.are.you.sure.you.want.to.edit')
        }
        onClose={() => {
          setConfirmDialog(false);
        }}
        onConfirm={() => {
          if (add) {
            handleSubmitAdd();
          } else {
            handleSubmitRemove();
          }

          onClose();
        }}
        cancelText={t('common:cancel')}
        confirmText={t('confirm')}
      />
    </div>
  );
}
